/**
 * Read-side formatting for the admin Sprint pages (LG-1004).
 *
 * The API ships Dates as ISO strings (no tRPC transformer), so every helper
 * takes either. All rendering is UTC: a Sprint week is defined in UTC and an
 * admin in any zone should read the same Monday.
 */

import type { SprintPhase } from '../../../lib/sprint/participation-transition'
import { lockInFor } from '../../../lib/sprint/week-math'

type DateLike = string | Date

function toDate(value: DateLike): Date {
  return value instanceof Date ? value : new Date(value)
}

/** The list page's phases: finer than the transition table's three. */
export type SprintUiPhase = 'upcoming' | 'locking' | 'running' | 'ended'

export const PHASE_LABEL: Record<SprintUiPhase, string> = {
  upcoming: 'Upcoming',
  locking: 'Locked',
  running: 'Current',
  ended: 'Closed',
}

/** The list page reads the clock itself: one `now` across every row. */
export function phaseOf(
  now: Date,
  sprint: { startsAt: DateLike; locksAt: DateLike; endsAt: DateLike },
): SprintUiPhase {
  if (now >= toDate(sprint.endsAt)) return 'ended'
  if (now >= toDate(sprint.startsAt)) return 'running'
  if (now >= toDate(sprint.locksAt)) return 'locking'
  return 'upcoming'
}

/**
 * The detail page has no clock of its own: the server decides the phase
 * and which Sprint is running, so the badge and the roster can never
 * disagree about whether lock-in has passed.
 */
export function uiPhaseOf(
  phase: SprintPhase,
  isRunning: boolean,
): SprintUiPhase {
  if (phase === 'after_end') return 'ended'
  if (phase === 'before_lock_in') return 'upcoming'
  return isRunning ? 'running' : 'locking'
}

const MONTH_DAY = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  timeZone: 'UTC',
})

const WEEKDAY_MONTH_DAY = new Intl.DateTimeFormat('en-US', {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
  timeZone: 'UTC',
})

const MONTH_DAY_YEAR = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
})

const TIME = new Intl.DateTimeFormat('en-US', {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
  timeZone: 'UTC',
})

/** "Sep 14 – Sep 20": `endsAt` is the next Monday, so the last day is a day before it. */
export function formatWeek(startsAt: DateLike, endsAt: DateLike): string {
  const lastDay = new Date(toDate(endsAt).getTime() - 24 * 60 * 60 * 1000)
  return `${MONTH_DAY.format(toDate(startsAt))} – ${MONTH_DAY.format(lastDay)}`
}

type LockWindow = { startsAt: DateLike; locksAt: DateLike }

/**
 * "Sat, Sep 12": the materializer's `lockInFor` sets every Sprint's lock
 * time, so it is only spelled out ("Sat, Sep 12, 15:00 UTC") when a Sprint
 * has been given a different one.
 */
export function formatLockMoment(sprint: LockWindow): string {
  const at = toDate(sprint.locksAt)
  const day = WEEKDAY_MONTH_DAY.format(at)
  const isDefault =
    at.getTime() === lockInFor(toDate(sprint.startsAt)).getTime()
  return isDefault ? day : `${day}, ${TIME.format(at)} UTC`
}

/** "locks Sat, Sep 12" until it has, then "locked …". */
export function formatLockLine(now: Date, sprint: LockWindow): string {
  const verb = now >= toDate(sprint.locksAt) ? 'locked' : 'locks'
  return `${verb} ${formatLockMoment(sprint)}`
}

/** `formatLockLine` for the detail page, where the server names the phase. */
export function formatLockLineFor(
  phase: SprintPhase,
  sprint: LockWindow,
): string {
  const verb = phase === 'before_lock_in' ? 'locks' : 'locked'
  return `${verb} ${formatLockMoment(sprint)}`
}

/** "Jun 1, 2026" */
export function formatDay(value: DateLike): string {
  return MONTH_DAY_YEAR.format(toDate(value))
}
